import React from 'react';
import { ExternalLink, PlayCircle } from 'lucide-react';
import { YouTubeEmbed } from './YouTubeEmbed';

export const VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE = 'Video has been removed from YouTube';

export function extractYouTubeVideoId(videoId?: string, url?: string): string | null {
  const trimmedId = videoId?.trim();
  if (trimmedId) return trimmedId;

  if (!url?.trim()) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host === 'youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0];
      return id || null;
    }
    if (host.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.split('/')[2] || null;
      }
      return parsed.searchParams.get('v');
    }
  } catch {
    return null;
  }

  return null;
}

export function isYouTubeVideoEmbeddable(embeddable?: boolean): boolean {
  return embeddable !== false;
}

type LessonVideoPlayerProps = {
  videoId?: string;
  title?: string;
  channelTitle?: string;
  url?: string;
  embeddable?: boolean;
  compact?: boolean;
};

export function LessonVideoPlayer({
  videoId,
  title,
  channelTitle,
  url,
  embeddable,
  compact = false,
}: LessonVideoPlayerProps) {
  const resolvedVideoId = extractYouTubeVideoId(videoId, url);
  const watchUrl = url?.trim() || (resolvedVideoId ? `https://www.youtube.com/watch?v=${resolvedVideoId}` : undefined);
  const canEmbed = isYouTubeVideoEmbeddable(embeddable) && !!resolvedVideoId;
  const displayTitle = title?.trim() || 'Video';

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className={compact ? 'text-sm' : undefined}>
        <p className={compact ? 'font-medium text-gray-900' : 'text-sm font-semibold text-gray-900'}>
          {displayTitle}
        </p>
        {channelTitle ? (
          <p className={compact ? 'text-xs text-gray-500' : 'text-xs text-gray-500'}>{channelTitle}</p>
        ) : null}
      </div>

      {canEmbed ? (
        <YouTubeEmbed
          videoId={resolvedVideoId}
          title={displayTitle}
          className="mx-auto w-full min-w-[356px] max-w-[480px]"
        />
      ) : null}

      {watchUrl ? (
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={
            compact
              ? 'inline-flex items-center gap-1 text-sm text-[#1a5172] underline underline-offset-2 hover:text-[#164a65]'
              : 'inline-flex items-center gap-1.5 text-sm font-medium text-[#1a5172] underline underline-offset-2 hover:text-[#164a65]'
          }
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {canEmbed ? 'Open on YouTube' : 'Watch on YouTube'}
        </a>
      ) : null}
    </div>
  );
}

export function LessonVideoRemovedPlaceholder({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={
        compact
          ? 'flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-600'
          : 'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-600'
      }
      role="status"
    >
      <PlayCircle
        className={compact ? 'h-5 w-5 shrink-0 text-gray-400' : 'h-8 w-8 text-gray-400'}
        aria-hidden
      />
      <p>{VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE}</p>
    </div>
  );
}
